import React, { useEffect, useState } from 'react';
import { useForm, UseFormSetValue } from 'react-hook-form';
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { CalcularMontosDto } from '../../models/participantes';
import { useParticipantes } from '../../hooks/useParticipantes';
import { enumTipoParticipanteConValores, enumTipoSeguroConValores, StatusCode, TipoFuncionalidad, TipoSeguro } from '../../../../utils/enums';
import { SeguroDeudorEdit } from '../../models/seguroDeudorEdit';
import ModalPrimaCobertura from './modalPrimaCobertura';
import { insertSeguroDeudor, updateSeguroDeudor } from '../../services/servicesDeudores';
import { useToast } from '../../../../hooks/useToast';
import { AxiosResponse } from 'axios';
import { useDeudor } from '../../hooks/useDeudor';
import { SeguroRow } from './listaSeguros';
import { Dialog, DialogContent, DialogTitle, Grid2 } from '@mui/material';
import MButton from '../../../../components/inputs/yup/materialui/mButton';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import CloseIcon from '@mui/icons-material/Close';
import InputSelect from '../../../../components/inputs/yup/materialui/inputSelect';
import InputNumber from '../../../../components/inputs/yup/materialui/inputNumber';
import InputText from '../../../../components/inputs/yup/materialui/inputText';
import ConfirmationModal from '../../../../components/ui/materialui/confirmationModal';
import { useOperacion } from '../../hooks/useOperacion';
import { DetalleMutuoPermissionGuard } from '../../../../components/auth/DetalleMutuoPermissionGuard';

interface IProps {
    seguroDeudorEdit: SeguroDeudorEdit;
    showModal: boolean;
    onHideModal: () => void;
    datosEditar: boolean;
    segurosRows: SeguroRow[];
    render: () => void;
    idMutuo: number;
    esVigente: boolean;
}

const SeguroDeudorForm: React.FC<IProps> = ({ seguroDeudorEdit, showModal, onHideModal, datosEditar, segurosRows, render, idMutuo, esVigente }) => {

    const getRounded = (value: number, d: number) => {
        const factor = Math.pow(10, d);
        return Math.round(value * factor) / factor;
    }

    const schemaSeguroDeudorEdit = Yup.object<SeguroDeudorEdit>().shape({
        mutuoId: Yup.number().required(),
        seguroId: Yup.number().required(),
        tipoSeguro: Yup.string().required('Campo requerido').test({
            name: 'valida-cesantía-desgravamen-tabla-desarrollo-minima',
            exclusive: false,
            params: {},
            message: 'Error',
            test: async (value, { createError }) => {
                let respuesta: boolean = true;

                if (value === enumTipoSeguroConValores.CESANTIA.toString()) {
                    respuesta = segurosRows.some(x => x.TipoSeguro === enumTipoSeguroConValores.DESGRAVAMEN.toString())
                    if (!respuesta) {
                        return createError({ message: "Debe ingresar el seguro de desgravamen, antes del cesantía" })
                    }

                    respuesta = segurosRows.some(x => x.TieneCuotaMinima === false && x.TipoSeguro === enumTipoSeguroConValores.DESGRAVAMEN.toString())
                    if (respuesta) {
                        return createError({ message: "Debe ingresar la tabla de desarrollo cuota minima para el seguro cesantía" })
                    }
                }

                if (value === enumTipoSeguroConValores.DESGRAVAMEN.toString()) {
                    if (respuesta = segurosRows.some(x => x.TipoSeguro === enumTipoSeguroConValores.DESGRAVAMEN.toString()) && !datosEditar) {
                        return createError({ message: "Ya existe un seguro desgravamen" })
                    }
                }

                return true;
            }
        }), // si es cesantía debo ver si tiene desgravamen, si es cesantía tabla desarrollo minima
        codigoPoliza: Yup.string().required('Campo requerido'),
        codigoInterno: Yup.string().required('Campo requerido').test({
            message: 'Error',
            test: async (value, { createError, parent }) => {
                const codigoPoliza = parent.codigoPoliza;
                if (!codigoPoliza) {
                    return createError({ message: 'Código póliza es requerido para validar código interno' });
                }

                if (segurosRows.some(x => x.CodigoPoliza === codigoPoliza && x.CodigoHijo === value && x.SeguroId !== seguroDeudorEdit.seguroId)) {
                    return createError({ message: 'Póliza seguro ya se encuentra asociada' });
                }

                return true;
            }
        }), // evaluar si ya los codigo poliza existen 
        tasa: Yup.number().required('Campo requerido'),
        montoPrimaAnual: Yup.number().required('Campo requerido'),
        montoAsegurado: Yup.number().required('Campo requerido').min(0, "Campo no puede ser menor que 0"),
        mesesGratuidad: Yup.number().required('Campo requerido').test({
            message: 'Error',
            test: async (value, { createError }) => {

                // La validación sólo se aplica si los meses de gratuidad son mayores a 0
                if (value > 0) {
                    if (seguroDeudorEdit.mesesGracia &&
                        seguroDeudorEdit.mesesGracia > 0 &&
                        value < seguroDeudorEdit.mesesGracia) {
                        return createError({ message: 'Meses gratuidad no pueden ser menor a los meses gracia' });
                    }

                    if (segurosRows.some(x => x.MesesGracias && x.MesesGracias > 0 && value < x.MesesGracias)) {
                        return createError({ message: 'Meses gratuidad no pueden ser menor a los meses gracia' });
                    }
                }

                return true;
            }
        }),// meses gratuidad, evaluar si no es menor a los de gracia
        nombreCompania: Yup.string().nullable(),
        montoPrimaDividendo: Yup.number().required('Campo requerido'),
        cantidadCuotasAnuales: Yup.number().required('Campo requerido'),
        tasaSobrePrima: Yup.number().required('Campo requerido'),
        deudaDirecta: Yup.number().required('Campo requerido'),
        deudaSeguro: Yup.number().required('Campo requerido'),
        tipoSubsidio: Yup.string().nullable(),
    });

    const { getPolizas, getPolizasInterna, getSeguroParticipante, setPolizasHijo, polizas, polizasHijo, calcularMonto } = useParticipantes();
    const { obtenerTiposSeguros, tipoSeguros, obtenerMontoAsegurado, montoAseguradoCalculado } = useDeudor();
    const { listaTipoSubsidio, tipoSubsidios } = useOperacion();
    const [show, setShow] = useState<boolean>(false);
    const [tienePrima, setTienePrima] = useState<boolean>(false);
    const { addToast } = useToast();
    const [modalConfirmacion, setModalConfirmacion] = useState({
        show: false,
        title: '',
        message: '',
        metodo: () => {
        },
    });

    const {
        formState: { errors, isValid, isDirty },
        handleSubmit,
        control,
        setValue,
        watch,
        reset,
        trigger
    } = useForm<SeguroDeudorEdit>({
        defaultValues: seguroDeudorEdit,
        mode: 'onChange',
        resolver: yupResolver(schemaSeguroDeudorEdit)
    })

    const dataFormSeguroDeudor = watch();

    useEffect(() => {
        obtenerTiposSeguros();
        listaTipoSubsidio();
    }, [])

    useEffect(() => {
        reset(seguroDeudorEdit);



    }, [seguroDeudorEdit, reset, showModal])

    useEffect(() => {
        if (showModal) {
            trigger();
        }

    }, [showModal])

    useEffect(() => {
        if (seguroDeudorEdit.tipoSeguro)
            getPolizas(seguroDeudorEdit.tipoSeguro);

    }, [seguroDeudorEdit.tipoSeguro]);

    useEffect(() => {
        if (seguroDeudorEdit?.codigoPoliza)
            getPolizasInterna(seguroDeudorEdit?.codigoPoliza, seguroDeudorEdit.tipoSeguro);

        if (seguroDeudorEdit.codigoPoliza && seguroDeudorEdit.codigoInterno) {
            handlePrima();
        }

    }, [seguroDeudorEdit]);

    const handlePrima = async () => {
        const resp = await getSeguroParticipante(dataFormSeguroDeudor.codigoPoliza, seguroDeudorEdit.codigoInterno);
        if (resp) {
            setTienePrima(resp.tienePrima!);
        }
    }

    const handleCodigoPoliza = (value: string) => {
        if (value)
            getPolizasInterna(value, dataFormSeguroDeudor.tipoSeguro);
        else
            setPolizasHijo([]);

        setValue("codigoInterno", "");
        resetDatosSeguro(setValue);
        setTienePrima(false)
    }

    const handleSeguro = async (codigoInternoPolizaSeleccionado: string) => {

        resetDatosSeguro(setValue);

        const resp = await getSeguroParticipante(dataFormSeguroDeudor.codigoPoliza, codigoInternoPolizaSeleccionado);

        if (resp) {
            setValue("tasa", resp.tasa!);
            setValue("nombreCompania", resp.nombreCompania);
            setTienePrima(resp.tienePrima!);
        }
    }

    const handleCalcularMonto = async () => {

        let data: CalcularMontosDto = {
            tipoParticipante: enumTipoParticipanteConValores.Deudor,
            idMutuo: idMutuo,
            codigoPoliza: dataFormSeguroDeudor.codigoPoliza ?? '',
            codigoHijo: dataFormSeguroDeudor.codigoInterno ?? '',
            porcentajeDeudaSeguro: dataFormSeguroDeudor.deudaSeguro ?? 0,
            tasaCalcular: dataFormSeguroDeudor.tasa ?? 0,
            montoAsegurado: seguroDeudorEdit.montoAsegurado ?? 0,
            montoCredito: dataFormSeguroDeudor.montoAsegurado ?? 0
        }

        const resp = await calcularMonto(data);
        if (resp) {
            setValue("montoAsegurado", resp.montoAsegurado);
            setValue("montoPrimaDividendo", resp.montoPrima);
            setValue("montoPrimaAnual", resp.montoPrimaAnual);
        }
    }

    const onHide = () => setShow(false);

    useEffect(() => {
        const prima = dataFormSeguroDeudor.montoAsegurado *
            (dataFormSeguroDeudor.tasa / 1000) *
            (dataFormSeguroDeudor.deudaSeguro / 100) *
            (dataFormSeguroDeudor.deudaDirecta / 100) || 0;

        const primaRedondeada = getRounded(prima, 4);
        const primaAnual = primaRedondeada * (dataFormSeguroDeudor?.cantidadCuotasAnuales ?? 12);

        setValue("montoPrimaDividendo", primaRedondeada);
        setValue("montoPrimaAnual", primaAnual);
    }, [dataFormSeguroDeudor.montoAsegurado,
    dataFormSeguroDeudor.tasa,
    dataFormSeguroDeudor.cantidadCuotasAnuales,
    dataFormSeguroDeudor.deudaSeguro,
    dataFormSeguroDeudor.deudaDirecta
    ]);

    const onSubmit = async (data: SeguroDeudorEdit) => {
        try {
            data.mutuoId = idMutuo;

            const response: AxiosResponse = datosEditar ?
                await updateSeguroDeudor(data) : await insertSeguroDeudor(data);

            if (response.status === StatusCode.Ok || response.status === StatusCode.Created || response.status === StatusCode.NoContent) {
                addToast({
                    mostrar: true,
                    titulo: 'Confirmación',
                    tipo: 'Success',
                    mensaje: datosEditar ?
                        'Datos modificados correctamente' :
                        'Datos guardados correctamente'
                });
                render();
                onHideModal();
            }
        } catch {
            console.error('Error');
        }
    }

    const handleCancelar = () => {
        if (isDirty) {
            setModalConfirmacion({
                show: true,
                title: "Hay cambios sin guardar",
                message: datosEditar ?
                    "¿Desea abandonar la edición?" :
                    '¿Desea abandonar el ingreso?',
                metodo: () => {
                    onHideModal();
                    reset();
                },
            });
        }
        else {
            onHideModal();
            reset();
        }
    }

    useEffect(() => {
        if (montoAseguradoCalculado) {
            setValue("montoAsegurado", montoAseguradoCalculado);
        }

    }, [montoAseguradoCalculado]);

    const handleTipoSeguro = (value: string | null) => {
        if (value) {
            getPolizas(value);
            obtenerMontoAsegurado(idMutuo, value);
        }
    }

    return (
        <>
            <ConfirmationModal
                onHide={() =>
                    setModalConfirmacion({
                        ...modalConfirmacion,
                        show: false,
                    })
                }
                onConfirm={modalConfirmacion.metodo}
                show={modalConfirmacion.show}
                title={modalConfirmacion.title}
                message={modalConfirmacion.message}
            />

            <Dialog
                open={showModal}
                onClose={onHideModal}
                maxWidth='lg'
            >
                <DialogTitle>
                    Seguro deudor
                </DialogTitle>
                <DialogContent>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Grid2 container spacing={1} sx={{ mb: 3, mt: 2 }}>
                            <Grid2 size={{ lg: 3, xs: 12 }} sx={{ mt: 3 }}>
                                <InputSelect
                                    label='Tipo seguro'
                                    name="tipoSeguro"
                                    control={control}
                                    options={tipoSeguros}
                                    disabled={datosEditar}
                                    error={errors.tipoSeguro}
                                    onChange={(value) => handleTipoSeguro(value)}
                                />
                            </Grid2>
                            <Grid2 size={{ lg: 3, xs: 12 }} sx={{ mt: 3 }}>
                                <InputSelect
                                    label='Código póliza'
                                    name="codigoPoliza"
                                    control={control}
                                    options={polizas}
                                    error={errors.codigoPoliza}
                                    onChange={(value) => handleCodigoPoliza(value ?? '')}
                                />
                            </Grid2>
                            <Grid2 size={{ lg: 3, xs: 12 }} sx={{ mt: 3 }}>
                                <InputSelect
                                    label='Código interno'
                                    name="codigoInterno"
                                    control={control}
                                    options={polizasHijo}
                                    error={errors.codigoInterno}
                                    onChange={(codigoInternoPolizaSeleccionado) => handleSeguro(codigoInternoPolizaSeleccionado ?? '')}
                                />
                            </Grid2>
                            <Grid2 size={{ lg: 3, xs: 12 }} sx={{ mt: 3 }}>
                                <InputNumber
                                    label='Tasa'
                                    name="tasa"
                                    control={control}
                                    error={errors.tasa}
                                    maxValue={100}
                                    suffix='%'
                                    decimales={4}
                                    minValue={0}
                                />
                            </Grid2>
                            <Grid2 size={{ lg: 3, xs: 12 }} sx={{ mt: 3 }}>
                                <InputNumber
                                    label='Monto prima anual'
                                    name="montoPrimaAnual"
                                    control={control}
                                    error={errors.montoPrimaAnual}
                                    minValue={0}
                                    decimales={4}
                                    maxValue={1000000}
                                    readonly
                                    prefix='UF'
                                />
                            </Grid2>
                            <Grid2 size={{ lg: 3, xs: 12 }} sx={{ mt: 3 }}>
                                <InputNumber
                                    label='Monto asegurado'
                                    name="montoAsegurado"
                                    control={control}
                                    error={errors.montoAsegurado}
                                    prefix='UF'
                                    decimales={4}
                                    // readonly
                                    minValue={0}
                                />
                            </Grid2>
                            <Grid2 size={{ lg: 3, xs: 12 }} sx={{ mt: 3 }}>
                                <InputNumber
                                    label='Meses gratuidad'
                                    name="mesesGratuidad"
                                    control={control}
                                    error={errors.mesesGratuidad}
                                    decimales={0}
                                    minValue={0}
                                    maxValue={999}
                                // maxValue={360}
                                // suffix='meses'
                                />
                            </Grid2>
                            <Grid2 size={{ lg: 3, xs: 12 }} sx={{ mt: 3 }}>
                                <InputText
                                    label='Nombre compañía seguros'
                                    name="nombreCompania"
                                    control={control}
                                    error={errors.nombreCompania}
                                    desabilitado
                                />
                            </Grid2>
                            <Grid2 size={{ lg: 3, xs: 12 }} sx={{ mt: 3 }}>
                                <InputNumber
                                    label='Monto prima dividendo'
                                    name="montoPrimaDividendo"
                                    control={control}
                                    error={errors.montoPrimaDividendo}
                                    decimales={4}
                                    maxValue={1000000}
                                    readonly
                                    prefix='UF'
                                />
                            </Grid2>
                            <Grid2 size={{ lg: 3, xs: 12 }} sx={{ mt: 3 }}>
                                <InputNumber
                                    label='Cantidad cuotas anuales'
                                    name="cantidadCuotasAnuales"
                                    control={control}
                                    error={errors.cantidadCuotasAnuales}
                                    minValue={0}
                                    decimales={0}
                                    readonly
                                    maxValue={1000000}
                                />
                            </Grid2>
                            <Grid2 size={{ lg: 3, xs: 12 }} sx={{ mt: 3 }}>
                                <InputNumber
                                    label='Tasa sobreprima'
                                    name="tasaSobrePrima"
                                    control={control}
                                    error={errors.tasaSobrePrima}
                                    maxValue={100}
                                    decimales={4}
                                    suffix='%'
                                />
                            </Grid2>
                            <Grid2 size={{ lg: 3, xs: 12 }} sx={{ mt: 3 }}>
                                <InputNumber
                                    label='Deuda directa'
                                    name="deudaDirecta"
                                    control={control}
                                    error={errors.deudaDirecta}
                                    maxValue={100}
                                    decimales={4}
                                    suffix='%'
                                />
                            </Grid2>
                            <Grid2 size={{ lg: 3, xs: 12 }} sx={{ mt: 3 }}>
                                <InputNumber
                                    label='Deuda seguro'
                                    name="deudaSeguro"
                                    control={control}
                                    error={errors.deudaSeguro}
                                    maxValue={100}
                                    decimales={4}
                                    suffix='%'
                                />
                            </Grid2>
                            {dataFormSeguroDeudor.tipoSeguro === TipoSeguro.CESANTIA &&
                                (<Grid2 size={{ lg: 3, xs: 12 }} sx={{ mt: 3 }}>
                                    <InputSelect
                                        label='Tipo subsidio'
                                        name="tipoSubsidio"
                                        control={control}
                                        options={tipoSubsidios}
                                        error={errors.tipoSubsidio}
                                    />
                                </Grid2>)
                            }

                            {tienePrima &&
                                <Grid2 size={{ lg: 4, xs: 8 }} sx={{ mt: 5 }}>
                                    <MButton
                                        type='button'
                                        variant='contained'
                                        onClick={() => setShow(true)}
                                    >
                                        Prima por cobertura
                                    </MButton>
                                </Grid2>
                            }
                        </Grid2>


                        <Grid2 container spacing={1} marginTop={2} justifyContent={"flex-end"}>
                            <DetalleMutuoPermissionGuard
                                esVigente={esVigente}
                                permissions={[TipoFuncionalidad.Bandeja_editar, TipoFuncionalidad.Cartera_editar_info]}>
                                <MButton
                                    variant='contained'
                                    disabled={!(isValid && isDirty)}
                                    onClick={handleSubmit(onSubmit)}
                                    startIcon={<SaveAltIcon />}
                                >
                                    Guardar
                                </MButton>
                            </DetalleMutuoPermissionGuard>
                            <MButton
                                variant='outlined'
                                color='inherit'
                                startIcon={<CloseIcon />}
                                onClick={handleCancelar}
                            >
                                Cancelar
                            </MButton>
                        </Grid2>
                    </form>
                </DialogContent>
            </Dialog >

            <ModalPrimaCobertura
                show={show}
                onHide={onHide}
                codigo={dataFormSeguroDeudor.codigoPoliza}
                codigoInterno={dataFormSeguroDeudor.codigoInterno}
                tipoSeguro={dataFormSeguroDeudor.tipoSeguro}
                tienePrima={tienePrima}
                montoAsegurado={dataFormSeguroDeudor.montoAsegurado}
            />
        </>
    );
};

export default SeguroDeudorForm;

function resetDatosSeguro(setValue: UseFormSetValue<SeguroDeudorEdit>) {
    setValue("tasa", 0);
    setValue("montoPrimaAnual", 0);
    // setValue("mesesGratuidad", 0);
    setValue("nombreCompania", "");
    setValue("montoPrimaDividendo", 0);
}